import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCIES, REGION_LABELS } from '../utils/currencies';
import './CurrencySelect.css';

function CurrencySelect({ value, onChange, onClose }) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter currencies based on search
  const filteredCurrencies = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return CURRENCIES;
    
    return CURRENCIES.filter(({ code }) => {
      const name = t(`currency.${code}`, { defaultValue: '' }).toLowerCase();
      return code.toLowerCase().includes(query) || name.includes(query);
    });
  }, [searchQuery, t]);

  // Group currencies by region
  const groupedCurrencies = useMemo(() => {
    const groups = {
      africa: [],
      mena: [],
      global: []
    };
    
    filteredCurrencies.forEach(currency => {
      if (groups[currency.region]) {
        groups[currency.region].push(currency);
      }
    });
    
    return groups;
  }, [filteredCurrencies]);

  const getRegionLabel = (region) => {
    const labels = {
      africa: t('currencyRegionAfrica'),
      mena: t('currencyRegionMena'),
      global: t('currencyRegionGlobal')
    };
    return labels[region] || region;
  };

  const handleSelect = (code) => {
    onChange(code);
    if (onClose) onClose();
  };

  return (
    <div className="currency-select-modal" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="currency-select-header">
        <h3 className="currency-select-title">{t('selectCurrency')}</h3>
        {onClose && (
          <button className="currency-close-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </div>

      <div className="currency-search-wrapper">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('searchCurrency')}
          className="currency-search-input"
          autoFocus
        />
      </div>

      <div className="currency-list-container">
        {filteredCurrencies.length === 0 ? (
          <div className="currency-no-results">
            {t('noResults')}
          </div>
        ) : (
          <>
            {/* Africa */}
            {groupedCurrencies.africa.length > 0 && (
              <div className="currency-group">
                <div className="currency-group-header">{getRegionLabel('africa')}</div>
                <div className="currency-group-list">
                  {groupedCurrencies.africa.map(({ code }) => (
                    <button
                      key={code}
                      className={`currency-item ${value === code ? 'selected' : ''}`}
                      onClick={() => handleSelect(code)}
                    >
                      <span className="currency-name">{t(`currency.${code}`)}</span>
                      <span className="currency-code">({code})</span>
                      {value === code && <span className="currency-check">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* MENA */}
            {groupedCurrencies.mena.length > 0 && (
              <div className="currency-group">
                <div className="currency-group-header">{getRegionLabel('mena')}</div>
                <div className="currency-group-list">
                  {groupedCurrencies.mena.map(({ code }) => (
                    <button
                      key={code}
                      className={`currency-item ${value === code ? 'selected' : ''}`}
                      onClick={() => handleSelect(code)}
                    >
                      <span className="currency-name">{t(`currency.${code}`)}</span>
                      <span className="currency-code">({code})</span>
                      {value === code && <span className="currency-check">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Global */}
            {groupedCurrencies.global.length > 0 && (
              <div className="currency-group">
                <div className="currency-group-header">{getRegionLabel('global')}</div>
                <div className="currency-group-list">
                  {groupedCurrencies.global.map(({ code }) => (
                    <button
                      key={code}
                      className={`currency-item ${value === code ? 'selected' : ''}`}
                      onClick={() => handleSelect(code)}
                    >
                      <span className="currency-name">{t(`currency.${code}`)}</span>
                      <span className="currency-code">({code})</span>
                      {value === code && <span className="currency-check">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CurrencySelect;
